<script setup lang="ts">
import FeaturedIcon from '@/common/components/molecules/featuredIcon/FeaturedIcon.vue';
import BreadCrumbsButton from './BreadCrumbsButton.vue';
import BreadCrumbsModal from './breadCrumbsModal/BreadCrumbsModal.vue';
import { setup, type Props } from './breadCrumbs.setup';
import { SHOW_PARENT_TEXT_VALUES } from './breadCrumbs.define';

const props = withDefaults(defineProps<Props>(), {
  mode: 'common',
});

const {
  isShowModal,
  isBreadCrumbModalLoading,

  menuList,
  currentBreadCrumbId,
  filteredPaths,
  menuPosition,
  isMenuToggle,
  menuToggleIndex,

  currentInstance,
  lastInstanceButton,

  monitoringDashboard,
  currentRootMenu,

  activeModalMode,

  menuMouseOver,
  menuMouseClick,
  menuMouseLeave,
  menuClose,
} = setup(props);
</script>

<template>
  <!-- Common -->
  <template v-if="mode === 'common'">
    <template
      v-for="(menu, i) in filteredPaths"
      :key="menu.value"
    >
      <span
        v-if="
          menu.parentText && menu.parentValue && SHOW_PARENT_TEXT_VALUES.includes(menu.parentValue)
        "
        class="path-name"
      >
        {{ menu.parentText }}
      </span>

      <span
        v-if="i === 0"
        class="path-name"
      >
        {{ menu.text }}
      </span>

      <template v-else>
        <span class="path-divider">/</span>
        <bread-crumbs-button
          :bread-crumb-id="menu.value"
          :is-active="isMenuToggle && i === menuToggleIndex"
          :index="i"
          @mouseover="menuMouseOver"
          @click="menuMouseClick"
          @mouseleave="menuMouseLeave"
        >
          {{ menu.text }}
        </bread-crumbs-button>
      </template>
    </template>
  </template>

  <!-- Instance -->
  <template v-if="mode === 'instance'">
    <template
      v-for="(menu, i) in filteredPaths"
      :key="menu.value"
    >
      <span
        v-if="i === 0"
        class="path-name"
      >
        {{ menu.text }}
      </span>

      <template v-else>
        <span class="path-divider">/</span>
        <bread-crumbs-button
          :bread-crumb-id="menu.value"
          :is-active="isMenuToggle && i === menuToggleIndex"
          :index="i"
          @mouseover="menuMouseOver"
          @click="menuMouseClick"
          @mouseleave="menuMouseLeave"
        >
          {{ menu.text }}
        </bread-crumbs-button>
      </template>
    </template>

    <template v-if="lastInstanceButton">
      <span class="path-divider">/</span>
      <bread-crumbs-button
        :bread-crumb-id="lastInstanceButton.instanceType"
        :is-active="isMenuToggle && filteredPaths.length === menuToggleIndex"
        :index="filteredPaths.length"
        :instance-type="lastInstanceButton.instanceType"
        @mouseover="menuMouseOver($event, lastInstanceButton.isCluster)"
        @click="menuMouseClick($event, lastInstanceButton.isCluster)"
        @mouseleave="menuMouseLeave"
      >
        {{ lastInstanceButton.text }}
      </bread-crumbs-button>
    </template>
  </template>

  <!-- Dashboard -->
  <template v-if="props.mode === 'dashboard'">
    <span class="path-name">{{ currentRootMenu }}</span>
    <span class="path-divider">/</span>
    <bread-crumbs-button
      :bread-crumb-id="`${monitoringDashboard.dashboardId}`"
      :is-active="isMenuToggle"
      @mouseover="menuMouseOver"
      @click="menuMouseClick"
      @mouseleave="menuMouseLeave"
    >
      <featured-icon
        v-if="props.dashboardIcon"
        :icon-code="props.dashboardIcon"
      />
      {{ monitoringDashboard.dashboardName }}
    </bread-crumbs-button>
  </template>

  <slot name="rightContent" />

  <bread-crumbs-modal
    v-if="isShowModal"
    :current-bread-crumb-id="currentBreadCrumbId"
    :menu-list="menuList"
    :mode="activeModalMode"
    :is-menu-toggle="isMenuToggle"
    :menu-position="menuPosition"
    :current-instance="currentInstance"
    :loading="isBreadCrumbModalLoading"
    @leave="menuMouseLeave"
    @close="menuClose"
  />
</template>

<style lang="scss" scoped>
:deep(.featured-icon) {
  vertical-align: middle;
  margin-right: 2px;
}
</style>
